import { Placement } from '@atlassian/post-office-placement-remote';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { fireTrackEvent } from '@canvas/analytics-frontend';
import { CustomerImpact, CapabilityTier } from '@canvas/operational-types';
import { captureExceptionForCapability } from '@canvas/sentry-react';

import {
  checkShouldAutoDismiss,
  recordAutoDismissalExposure,
  readAutoDismissalState,
} from './auto-dismissal-store.ts';
import {
  type FeatureName,
  isFeatureEnabled,
  PLACEMENT_ID,
  messages,
} from './messages.ts';
import { getChangeboardingOverrideFeature } from './query.ts';
import { setMessageSeen, useIsMessageSeen } from './state.ts';
import { clearCache, useChangeboardingMessage } from './use-changeboarding-messages.tsx';
import { useIsChangeboardingActive } from './use-is-changeboarding-active.ts';

export { useIsChangeboardingActive };

interface ChangeboardingExperienceProps {
  children: (props: {
    onDismiss: () => void;
    onPrimaryAction: () => void;
  }) => React.ReactNode;
  feature: FeatureName;
  defaultRender?: React.ReactNode;
  onMount?: () => void;
  /**
   * Whether to fire an exposure event for auto-dismissal.
   * @default false
   */
  fireExposureForAutoDismissal?: boolean;
  className?: string;
  dismissOnTimeout?: number;
}

const OVERRIDE_ENVS = new Set(['dev']);

export const ChangeboardingExperience: React.FC<ChangeboardingExperienceProps> = ({
  children,
  feature,
  defaultRender,
  onMount,
  fireExposureForAutoDismissal = false,
  dismissOnTimeout,
}) => {
  const [autoDismissalChecked, setAutoDismissalChecked] = useState(false);
  const isMessageSeen = useIsMessageSeen(feature);
  const { placementProps, instanceId, loading } = useChangeboardingMessage(
    feature,
    isMessageSeen,
  );

  const postOfficeDismissRef = useRef<() => void>(() => {});

  const handleDismiss = useCallback(() => {
    setMessageSeen(feature, true);
    fireTrackEvent({
      action: 'dismissed',
      actionSubject: 'changeboardingMessage',
      source: 'board',
      attributes: {
        feature,
      },
    });
  }, [feature]);

  const handlePrimaryAction = useCallback(() => {
    setMessageSeen(feature, true);
    fireTrackEvent({
      action: 'primaryActionClicked',
      actionSubject: 'changeboardingMessage',
      source: 'board',
      attributes: { feature },
    });
  }, [feature]);

  const handleViewed = useCallback(() => {
    fireTrackEvent({
      action: 'viewed',
      actionSubject: 'changeboardingMessage',
      source: 'board',
      attributes: {
        feature,
      },
    });
  }, [feature]);

  const handleAutoDismiss = useCallback(async () => {
    try {
      const config = messages[feature]?.autoDismissal;
      if (!config) {
        return;
      }

      const overrideForFeature = getChangeboardingOverrideFeature();
      if (overrideForFeature === feature) {
        setAutoDismissalChecked(true);
        return;
      }

      // Only count exposures when explicitly requested by the caller
      const state = fireExposureForAutoDismissal
        ? await recordAutoDismissalExposure(feature)
        : await readAutoDismissalState(feature);

      const { shouldAutoDismiss, reason } = checkShouldAutoDismiss(feature, state);

      if (shouldAutoDismiss) {
        setMessageSeen(feature, true);
        // Fire auto-dismiss analytics and dismiss
        fireTrackEvent({
          action: 'autoDismissed',
          actionSubject: 'changeboardingMessage',
          source: 'board',
          attributes: {
            feature,
            reason,
            times: state.times,
            daysSeen: state.daysSeen,
          },
        });

        // Use the same dismissal path as manual dismiss to ensure
        // post-office is also dismissed
        try {
          postOfficeDismissRef.current?.();
          clearCache();
        } catch (e) {
          captureExceptionForCapability(
            e,
            {
              tags: {
                captureLocation: 'ChangeboardingExperience-autoDismiss-postOffice',
                customerImpact: CustomerImpact.CUSTOMERS,
              },
            },
            [
              {
                capability: {
                  importance: CapabilityTier.C3,
                  kebabCaseName: 'changeboarding',
                },
              },
            ],
          );
        }
      }
    } catch (e) {
      captureExceptionForCapability(
        e,
        {
          tags: {
            captureLocation: 'ChangeboardingExperience-autoDismiss-check',
            customerImpact: CustomerImpact.CUSTOMERS,
          },
        },
        [
          {
            capability: {
              importance: CapabilityTier.C3,
              kebabCaseName: 'changeboarding',
            },
          },
        ],
      );
    }

    setAutoDismissalChecked(true);
  }, [feature, fireExposureForAutoDismissal]);

  useEffect(() => {
    if (
      isMessageSeen ||
      loading ||
      !placementProps ||
      !instanceId ||
      !isFeatureEnabled(feature)
    ) {
      return;
    }

    if (!autoDismissalChecked) {
      handleAutoDismiss();
      return;
    }

    if (!postOfficeDismissRef.current) {
      return;
    }

    // If dismissOnTimeout is set, dismiss the message after the timeout
    let timeout: NodeJS.Timeout | undefined;
    if (dismissOnTimeout) {
      timeout = setTimeout(() => {
        postOfficeDismissRef.current();
        handleDismiss();
      }, dismissOnTimeout);
    } else {
      // Finally, we know that we can mount and handle viewing the message.
      onMount?.();
      handleViewed();
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    handleViewed,
    isMessageSeen,
    loading,
    placementProps,
    instanceId,
    feature,
    onMount,
    fireExposureForAutoDismissal,
    handleAutoDismiss,
    autoDismissalChecked,
    dismissOnTimeout,
    handleDismiss,
  ]);

  // In runtime before you refresh the page, there can be slight delays between
  // the messages API and our rendering, which means a dismissed message will
  // continue to show until you refresh the page or force an update in some way.
  // This local valtio state will prevent that in an active session.
  if (isMessageSeen) {
    return defaultRender ?? null;
  }

  if (loading || !placementProps || !instanceId || !postOfficeDismissRef.current) {
    // We don't want to trigger the placement component while we're still
    // loading props/instanceId for this feature.
    return defaultRender ?? null;
  }

  if (!isFeatureEnabled(feature)) {
    return defaultRender ?? null;
  }

  // Only spoof the _component_ itself in the dev environment.
  if (getChangeboardingOverrideFeature(OVERRIDE_ENVS) === feature) {
    return (
      <div>
        {children({
          onDismiss: () => {
            // No-op for override path, no need to trigger post-office dismissal.
            postOfficeDismissRef.current = () => {};
            handleDismiss();
          },
          onPrimaryAction: () => {
            postOfficeDismissRef.current = () => {};
            handlePrimaryAction();
          },
        })}
      </div>
    );
  }

  const finalPlacementProps = {
    ...placementProps,
    messageInstanceId: instanceId,
    // Part of a migration we're doing to rename fallbackChildren to
    // defaultRender. We'll remove fallbackChildren once we're confident both
    // bundles (CFE, Canvas) are using the new prop.
    fallbackChildren: defaultRender,
    defaultRender,
  };

  return (
    <Placement
      placementId={PLACEMENT_ID}
      placementProps={finalPlacementProps}
      Fallback={defaultRender ? (defaultRender as React.ReactElement) : undefined}
    >
      {
        ((postOfficeDismissMessage: () => void) => {
          // store for auto-dismiss path
          postOfficeDismissRef.current = () => {
            postOfficeDismissMessage();
          };

          return children({
            onDismiss: () => {
              handleDismiss();
              postOfficeDismissMessage();
            },
            onPrimaryAction: () => {
              handlePrimaryAction();
              postOfficeDismissMessage();
            },
          });
        }) as unknown as React.ReactNode
      }
    </Placement>
  );
};
